import { useEffect, type JSX } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { parseHelpParam } from './deep-link-parse';
import { openGuideHub, startTour } from './runtime';

/**
 * Handles `?help=` on any shell route, then removes it with a `replace` —
 * which is not a route change, so it never interrupts a running tour.
 *
 *   ?help=hub                       opens the hub
 *   ?help=tour:<key>[.<stepId>]     starts or arms that tour (guide CTAs, the bell)
 *   ?help=tip:<itemKey>             opens the hub on «پیشنهادها», focused on that tip
 *
 * An unknown key, or a tour the viewer may not see, does nothing at all.
 */
export function GuidanceDeepLinks(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get('help');
    if (value === null) return;
    params.delete('help');
    const search = params.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );

    const link = parseHelpParam(value);
    if (!link) return;
    if (link.kind === 'hub') openGuideHub(undefined, 'deeplink');
    else if (link.kind === 'tip') openGuideHub({ section: 'tips', id: link.id }, 'deeplink');
    else startTour(link.key, { source: 'deeplink', startAtStepId: link.stepId });
  }, [location.search, location.pathname, location.hash, location.state, navigate]);

  return null;
}
